import React, { createContext, useContext, useState, useEffect } from 'react'
import isEmpty from 'lodash/isEmpty'

interface WizardContextType {
  loading: boolean
  currentStep: string | null
  steps: string[]
  initialStep: string
  goBack: () => void
  goForward: () => void
  goTo: (stepName: string) => void
  restart: () => void
}

interface WizardProviderProps {
  onStepChange?: (step: string) => void
  initialStep?: string
  children: React.ReactNode
  steps: string[]
}

const WizardContext = createContext<WizardContextType | undefined>(undefined)

const WizardProvider: React.FC<WizardProviderProps> = ({ onStepChange = () => {}, initialStep, children, steps }) => {
  const [currentStep, setCurrentStep] = useState<string | null>(initialStep || null)
  const [stepHistory, setStepHistory] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // this means data is still fetching
    if (!currentStep && initialStep) {
      setCurrentStep(initialStep)
      setLoading(false)
    }
    // this means the data is re-hydrated
    if (currentStep) {
      setLoading(false)
    }
  }, [currentStep, initialStep])

  const immutablePush = <T,>(arr: T[], newEntry: T): T[] => [...arr, newEntry]

  const immutablePop = <T,>(arr: T[]): T[] => arr.slice(0, -1)

  const store: WizardContextType = {
    loading,
    steps,
    initialStep: steps.includes(initialStep || '') ? initialStep || steps[0] : steps[0],
    currentStep,
    goBack: () => {
      const currentStepsIndex = currentStep ? steps.indexOf(currentStep) : -1
      const newStep = steps[Math.max(0, currentStepsIndex - 1)]

      if (isEmpty(stepHistory)) {
        setCurrentStep(newStep)
        onStepChange(newStep)
        return
      }

      const lastVisitedStep = stepHistory[stepHistory.length - 1]

      setCurrentStep(lastVisitedStep)
      onStepChange(lastVisitedStep)
      setStepHistory(immutablePop(stepHistory))
    },
    goForward: () => {
      if (currentStep) {
        setStepHistory(immutablePush(stepHistory, currentStep))
      }

      const current = currentStep ? steps.indexOf(currentStep) : -1
      const maxStep = steps.length - 1
      const newStep = steps[Math.min(maxStep, current + 1)]

      setCurrentStep(newStep)
      onStepChange(newStep)
    },
    goTo: (stepName: string) => {
      if (steps.includes(stepName)) {
        if (currentStep) {
          setStepHistory(immutablePush(stepHistory, currentStep))
        }
        setCurrentStep(stepName)
        onStepChange(stepName)
      }
    },
    restart: () => {
      setStepHistory([])
      const firstStep = steps[0]
      setCurrentStep(firstStep)
      onStepChange(firstStep)
    }
  }

  return <WizardContext.Provider value={store}>{children}</WizardContext.Provider>
}

const useWizardProvider = (): WizardContextType => {
  const context = useContext(WizardContext)

  if (!context) {
    throw new Error('useWizardProvider must be used within an WizardProvider')
  }

  return context
}

export { WizardProvider, useWizardProvider, WizardContext }
